import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, AlertCircle } from 'lucide-react';
import RestroomIcon from './components/RestroomIcon';
import { CLEANING_THRESHOLD, DUE_SOON_AT, cleaningCycle, queueColor } from './facilitiesMetrics';
import '../Concession/concession.css';
import '../Gates/gates.css';
import './facilities.css';

// Restrooms location on the Locations page, in the same style as the gates card (no name or logo): today's visits,
// how many restrooms are clean and how many are close to a cleaning (every CLEANING_THRESHOLD visits),
// then a tile per restroom with its image, name and live queue.
// Tile bar colour: green, amber from halfway to the next cleaning, red once it's due soon
const cleaningBarColor = (cycle) => {
  if (cycle.dueSoon) return 'var(--q-bad)';
  if (cycle.since >= CLEANING_THRESHOLD / 2) return 'var(--q-warn)';
  return 'var(--q-good)';
};

// A restroom has queue sensors when its config says so, or its location records restroom queues
const hasQueueSensors = (restroom, location) =>
  !!restroom.multiSensor?.queue_time || (restroom.queueIds || []).length > 0 || !!location.queueMeasurement;

export default function FacilitiesCard({ locationData, data }) {
  const name = locationData.name;
  const restrooms = locationData.restrooms || [];
  const current = data?.restrooms || null;
  const hasData = current != null;
  const byId = new Map((current || []).map((r) => [String(r.restroomId), r]));

  // Same as the gates card: no entries recorded (or no reading yet) shows 0, not a dash
  const visits = (current || []).reduce((sum, r) => sum + (Number(r.entries) || 0), 0);
  const dueSoon = (current || []).filter((r) => cleaningCycle(Number(r.entries) || 0).dueSoon).length;
  const visitsText = visits.toLocaleString();
  const dueText = String(dueSoon);
  // Clean: every restroom not yet due soon
  const cleanText = String(Math.max(0, restrooms.length - dueSoon));

  // Restrooms stay in config order so each one keeps its place between refreshes
  const tiles = restrooms.map((r) => {
    const reading = byId.get(String(r.restroomId));
    const label = String(r.label || '').trim() || `Restroom ${r.restroomId}`;
    return {
      key: r.restroomId,
      label,
      section: r.sectionId != null ? `Section ${r.sectionId}` : null,
      title: r.sectionId != null ? `${label}, Section ${r.sectionId}` : label,
      imageURL: r.imageURL || null,
      // Same as the concessions and gates cards: a stale or missing reading shows 0 min;
      // only restrooms without queue sensors show a dash
      queue: hasQueueSensors(r, locationData) ? Math.round(Number(reading?.queueTime) || 0) : null,
      cycle: cleaningCycle(Number(reading?.entries) || 0),
    };
  });

  return (
    <Link
      to={`../locations/facilities/${name.replace(/ /g, '-').toLowerCase()}`}
      state={{
        id: locationData?.id || 'facilities-overview',
        currentLocation: locationData,
      }}
      className="cx-card gx-card-wide"
      aria-label={name}
    >
      {/* No name or logo: the section title already says what this is, and each restroom shows its own image */}
      <div className="gx-card-main">
        <div className="cx-card-metric">
          <div className="cx-metric-label">Entries today</div>
          <div className={`cx-card-value${hasData ? '' : ' gx-card-empty'}`}>{visitsText}</div>
        </div>
      </div>

      {/* Clean / due soon, with the check and alert icons from the original card */}
      <div
        className="fx-card-status"
        title={`Due soon: within ${CLEANING_THRESHOLD - DUE_SOON_AT} entries of a cleaning (one every ${CLEANING_THRESHOLD} entries)`}
      >
        <div className="fx-card-status-tile fx-card-status-clean">
          <span className="fx-card-status-count">
            <CheckCircle size={20} />
            {cleanText}
          </span>
          <span className="fx-card-status-label">Clean</span>
        </div>
        <div className={`fx-card-status-tile fx-card-status-due${dueSoon ? ' is-active' : ''}`}>
          <span className="fx-card-status-count">
            <AlertCircle size={20} />
            {dueText}
          </span>
          <span className="fx-card-status-label">Due soon</span>
        </div>
      </div>

      {tiles.length > 0 && (
        <div className="gx-gates">
          <div className="gx-tiles">
            {tiles.map((t) => (
              <div
                className="gx-tile fx-tile"
                key={t.key}
                title={[
                  t.title,
                  t.queue == null ? 'no queue reading' : `${t.queue} min queue`,
                  t.cycle ? `${t.cycle.since} of ${CLEANING_THRESHOLD} entries toward the next cleaning` : null,
                ].filter(Boolean).join(' · ')}
              >
                <RestroomIcon imageURL={t.imageURL} className="fx-tile-icon" />
                <span className="gx-tile-name">
                  {t.label}
                  {t.section && <span className="fx-tile-sub">{t.section}</span>}
                </span>
                {t.queue == null ? (
                  <span className="gx-tile-queue gx-card-empty">—</span>
                ) : (
                  <span className="gx-tile-queue" style={{ color: queueColor(t.queue) }}>
                    {t.queue}
                    <span className="gx-tile-unit">min</span>
                  </span>
                )}
                {/* How close it is to its next cleaning: green, amber from halfway, red when due soon */}
                {t.cycle && (
                  <span className="fx-tile-bar" aria-hidden="true">
                    <i style={{ width: `${Math.max(2, t.cycle.progress * 100)}%`, background: cleaningBarColor(t.cycle) }} />
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Link>
  );
}
